import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// Thunks
export const fetchTiposBombero = createAsyncThunk(
  'tiposBombero/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tipos-bombero')
      return response.data.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al obtener tipos de bombero'
      )
    }
  }
)

export const createTipoBombero = createAsyncThunk(
  'tiposBombero/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/tipos-bombero', data)
      return response.data.data
    } catch (error) {
      // El interceptor ya procesa el error, así que error puede ser el data o mensaje
      return rejectWithValue(
        error?.message || error || 'Error al crear tipo de bombero'
      )
    }
  }
)

export const updateTipoBombero = createAsyncThunk(
  'tiposBombero/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/tipos-bombero/${id}`, data)
      return response.data.data
    } catch (error) {
      return rejectWithValue(
        error?.message || error || 'Error al actualizar tipo de bombero'
      )
    }
  }
)

export const deleteTipoBombero = createAsyncThunk(
  'tiposBombero/delete',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/tipos-bombero/${id}`)
      return id
    } catch (error) {
      return rejectWithValue(
        error?.message || error || 'Error al eliminar tipo de bombero'
      )
    }
  }
)

export const recalcularTiposBomberos = createAsyncThunk(
  'tiposBombero/recalcular',
  async (_, { rejectWithValue }) => {
    try {
      // Timeout extendido a 5 minutos para recalcular muchos bomberos
      const response = await api.post('/tipos-bombero/recalcular', {}, { timeout: 300000 })
      return response.data
    } catch (error) {
      return rejectWithValue(
        error?.message || error || 'Error al recalcular tipos de bomberos'
      )
    }
  }
)

const initialState = {
  tiposBombero: [],
  loading: false,
  error: null,
  success: null,
  recalculando: false,
  recalculacionResult: null
}

const tiposBomberoSlice = createSlice({
  name: 'tiposBombero',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = null
    },
    clearRecalculacionResult: (state) => {
      state.recalculacionResult = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchTiposBombero.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTiposBombero.fulfilled, (state, action) => {
        state.loading = false
        state.tiposBombero = action.payload
      })
      .addCase(fetchTiposBombero.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create
      .addCase(createTipoBombero.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createTipoBombero.fulfilled, (state, action) => {
        state.loading = false
        state.tiposBombero.push(action.payload)
        state.tiposBombero.sort((a, b) => a.aniosRequeridos - b.aniosRequeridos)
        state.success = 'Tipo de bombero creado exitosamente'
      })
      .addCase(createTipoBombero.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update
      .addCase(updateTipoBombero.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateTipoBombero.fulfilled, (state, action) => {
        state.loading = false
        const index = state.tiposBombero.findIndex(t => t.id === action.payload.id)
        if (index !== -1) {
          state.tiposBombero[index] = action.payload
        }
        state.tiposBombero.sort((a, b) => a.aniosRequeridos - b.aniosRequeridos)
        state.success = 'Tipo de bombero actualizado exitosamente'
      })
      .addCase(updateTipoBombero.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Delete
      .addCase(deleteTipoBombero.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTipoBombero.fulfilled, (state, action) => {
        state.loading = false
        state.tiposBombero = state.tiposBombero.filter(t => t.id !== action.payload)
        state.success = 'Tipo de bombero eliminado exitosamente'
      })
      .addCase(deleteTipoBombero.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Recalcular
      .addCase(recalcularTiposBomberos.pending, (state) => {
        state.recalculando = true
        state.error = null
      })
      .addCase(recalcularTiposBomberos.fulfilled, (state, action) => {
        state.recalculando = false
        state.recalculacionResult = action.payload
        state.success = `Tipos recalculados: ${action.payload.actualizados} bomberos actualizados`
      })
      .addCase(recalcularTiposBomberos.rejected, (state, action) => {
        state.recalculando = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearSuccess, clearRecalculacionResult } = tiposBomberoSlice.actions
export default tiposBomberoSlice.reducer
